import Data, { treeNode } from '@/data'
import Trans from '@/data/trans'

import {
  evaluateTags,
  forceCondition,
  getValue,
  modMakeRange,
  modStatsMax,
  parseMods,
  parseModStats,
  RangeObject,
  statListFromModList,
  statsExtend
} from './mods'
import { Environment, LocalStats } from './stats'

import { ItemFlags } from '@/data/enums'
import {
  EssenceDefinition,
  ItemDefinition,
  ModDefinition,
  Range,
  UniqueDefinition,
  UniqueRandomMod
} from '@/data/types'
import { TooltipLine } from '@/types/common'
import { Item, ItemRarity, ItemTooltipOptions, StatType } from '@/types/item'
import { notNull } from '@/utils/validation'
import { tattooLimits, tattooTargetSets } from './tattoos'
import { uniqueModTypes } from './uniqueMods'

export function baseMaxQuality(id: string) {
  const base = Data.items[id]
  const template = Data.item_templates[base.inherits]
  return template.max_quality
}

export function itemCreateAbstract(base: string, source?: Item) {
  const properties: Item['properties'] = {}
  const baseItem = Data.items[base]
  if (baseItem?.properties) {
    for (const prop of ['armour', 'evasion', 'energy_shield'] as const) {
      const value = baseItem.properties[prop]
      if (value) properties[prop] = value.max
    }
  }
  return {
    base,
    ilvl: source?.ilvl ?? 100,
    quality: source?.quality ?? baseMaxQuality(base) ?? 0,
    flags: source?.flags ?? 0,
    sockets: source?.sockets ?? [],
    properties
  }
}

export function itemAlwaysCorrupted(item: Item) {
  if (item.unique) {
    return (Data.uniques[item.unique].flags & ItemFlags.Corrupted) != 0
  } else {
    return Data.items[item.base].implicits?.includes('TalismanCanBePickedUpByMonster')
  }
}

// MUTATES item itself, but not any objects inside the item
export function itemUpdateStats(item: Item, ...types: StatType[]) {
  if (!item.mods) return item
  item.stats = { ...item.stats }
  for (const type of Object.keys(item.mods) as StatType[]) {
    if (types.length && !types.includes(type)) continue
    const stats = statsExtend({}, ...Object.values(item.mods[type]!))
    if (type === 'implicit') {
      const base = Data.items[item.base]
      if (base) {
        for (const id of base.implicits) {
          const mod = Data.mods[id]
          if (!mod.visible) {
            statsExtend(stats, modStatsMax(mod))
          }
        }
      }
    }
    item.stats[type] = stats
    for (const id of Object.keys(item.mods[type]!)) {
      const mod = Data.mods[id]
      if (mod.generation_type === 'corrupted') item.flags |= ItemFlags.Corrupted
      if (mod.influence) item.flags |= mod.influence
    }
  }
  return itemFixSockets(item)
}

function itemDefaultImplicits(item: Item) {
  const unique = item.unique && Data.uniques[item.unique].variants[item.variant!]
  if (unique && unique.random_mods) {
    const implicits = unique.random_mods.find((r) => r.statType === 'implicit')
    if (implicits) return
  }
  const base = Data.items[item.base]
  if (base) return base.implicits.filter((id) => Data.mods[id]?.visible)
}

export function itemFixImplicit(item: Item) {
  if (!item.mods) return item
  // Item can have either corrupted or uncorrupted implicits, but not both
  // Uncorrupted items cannot have corrupted implicits
  let hasCorruptedMods = false
  let hasUncorruptedMods = false
  const ids = Object.keys(item.mods.implicit)
  for (const id of ids) {
    if (Data.mods[id].generation_type === 'corrupted') {
      hasCorruptedMods = true
    } else {
      hasUncorruptedMods = true
    }
  }
  let changed = false
  if (item.flags & ItemFlags.Corrupted) {
    if (hasCorruptedMods && hasUncorruptedMods) {
      changed = true
      item.mods = { ...item.mods }
      item.mods.implicit = Object.fromEntries(
        Object.entries(item.mods.implicit).filter(([id]) => Data.mods[id].generation_type === 'corrupted')
      )
    }
  } else {
    if (hasCorruptedMods) {
      changed = true
      item.mods = { ...item.mods }
      item.mods.implicit = Object.fromEntries(
        Object.entries(item.mods.implicit).filter(([id]) => Data.mods[id].generation_type !== 'corrupted')
      )
    }
  }
  const base = Data.items[item.base]
  if (!Object.keys(item.mods.implicit).length) {
    const implicits = itemDefaultImplicits(item)
    if (implicits) {
      if (!changed) {
        changed = true
        item.mods = { ...item.mods }
      }
      item.mods.implicit = {}
      for (const id of implicits) {
        item.mods.implicit[id] = modStatsMax(Data.mods[id])
      }
    }
  }
  if (changed) {
    itemUpdateStats(item, 'implicit')
  }
  return item
}

export function itemFixAtlasMods(item: Item) {
  if (!item.mods || !item.mods.explicit) return item
  const invalid_mod = (id: string) =>
    Data.mods[id].influence && (item.flags & Data.mods[id].influence) !== Data.mods[id].influence
  if (item.mods.explicit && Object.keys(item.mods.explicit).some(invalid_mod)) {
    item.mods = { ...item.mods }
    item.mods.explicit = Object.fromEntries(Object.entries(item.mods.explicit!).filter(([id]) => !invalid_mod(id)))
    itemUpdateStats(item, 'explicit')
  }
  return item
}

export function itemMaxSockets(item: Item, stats?: LocalStats) {
  let sockets = Data.item_classes[Data.items[item.base].item_class].sockets || 0
  if (!stats) stats = itemGetStats(item)
  sockets = Math.max(sockets, stats.aggregate('maximum').item_sockets ?? 0)
  if (stats.additive.item_no_sockets) sockets = 0

  const abyss = stats.raw.local_has_X_abyss_sockets ?? 0

  return { normal: Math.max(0, sockets - abyss), abyss }
}

export function itemAbyssSockets(item: Item) {
  let count = 0
  for (const src of Object.values(item.stats).filter(notNull)) {
    count += src.local_has_X_abyss_sockets ?? 0
  }
  return count
}

export function itemWhiteSockets(item: Item, stats?: LocalStats) {
  if (!stats) stats = itemGetStats(item)
  return !!stats.additive.item_socket_colour?.white
}

export function itemDefaultSocketColor(item: Item, stats?: LocalStats) {
  const base = Data.items[item.base]
  if (itemWhiteSockets(item, stats)) {
    return 'white'
  } else if (base.requirements) {
    const str = base.requirements.strength || 0
    const dex = base.requirements.dexterity || 0
    const int = base.requirements.intelligence || 0
    if (str >= dex && str >= int) {
      return 'red'
    } else if (dex >= int) {
      return 'green'
    } else {
      return 'blue'
    }
  } else {
    return 'red'
  }
}

export function itemRemoveSocket(item: Item, index: number) {
  if (item.sockets.length <= 1) return item // can't remove last socket
  const link = item.sockets[index].link
  item = { ...item, sockets: item.sockets.slice() }
  item.sockets.splice(index, 1)
  if (index > 0 && !link && item.sockets[index - 1].link) {
    item.sockets[index - 1] = { ...item.sockets[index - 1], link: false }
  }
  return item
}

export function itemAddSocket(item: Item) {
  const stats = itemGetStats(item)
  if (item.sockets.length >= itemMaxSockets(item, stats).normal) return item // full
  item = { ...item, sockets: item.sockets.slice() }
  item.sockets.push({
    color: itemDefaultSocketColor(item, stats),
    link: false
  })
  if (item.sockets.length >= 2) {
    item.sockets[item.sockets.length - 2] = {
      ...item.sockets[item.sockets.length - 2],
      link: true
    }
  }
  return item
}

export function itemFixSockets(item: Item) {
  const stats = itemGetStats(item)
  const sockets = itemMaxSockets(item, stats)
  const defaultColor = itemDefaultSocketColor(item, stats)
  if (sockets.normal && !item.sockets.length) {
    item.sockets = [...Array(sockets)].map((_, i) => ({
      color: defaultColor,
      link: i < sockets.normal - 1
    }))
  } else if (item.sockets.length > sockets.normal) {
    item.sockets = item.sockets.slice(0, sockets.normal)
    if (item.sockets[item.sockets.length - 1]?.link) {
      item.sockets[item.sockets.length - 1] = {
        ...item.sockets[item.sockets.length - 1],
        link: false
      }
    }
  }
  if (sockets.abyss && !item.jewels) {
    item.jewels = [...Array(sockets.abyss)].map(() => undefined)
  } else if (item.jewels && item.jewels.length !== sockets.abyss) {
    item.jewels = item.jewels.slice(0, sockets.abyss)
    while (item.jewels.length < sockets.abyss) {
      item.jewels.push(undefined)
    }
  }
  // if (
  //   !(item.flags & ItemFlags.Corrupted) &&
  //   defaultColor !== 'white' &&
  //   item.sockets.some((sock) => sock.color === 'white')
  // ) {
  //   item.sockets = item.sockets.map((sock) => {
  //     if (sock.color !== 'white') return sock
  //     let color = sock.gem ? getGemColor(Data.gems[sock.gem.id]) : defaultColor
  //     if (color === 'white') color = defaultColor
  //     return { ...sock, color }
  //   })
  // }
  return item
}

export function itemCreateNormal(base: string, source?: Item) {
  const baseItem = Data.items[base]

  const item: Item = {
    ...itemCreateAbstract(base, source),
    name: source?.name,
    stats: {
      implicit: {},
      crafted: {},
      explicit: {},
      enchant: {},
      fractured: {}
    },
    rarity: source && !source.unique ? source.rarity : 'rare'
  }

  if (baseItem.tier) {
    item.map_tier = baseItem.tier
  }

  const template = Data.item_templates[baseItem.inherits]
  if (item.rarity === 'rare' && template.disable_rarity?.includes('rare')) {
    item.rarity = 'magic'
  }
  if (item.rarity === 'magic' && template.disable_rarity?.includes('magic')) {
    item.rarity = 'normal'
  }

  if (itemAlwaysCorrupted(item)) item.flags |= ItemFlags.Corrupted

  if (baseItem.cluster_jewel) {
    const { min_skills, max_skills } = baseItem.cluster_jewel
    item.clusterJewel = {
      nodes: Math.min(max_skills, Math.max(min_skills, source?.clusterJewel?.nodes ?? min_skills)),
      skill: baseItem.cluster_jewel.passive_skills[0].id
    }
    if (
      source?.clusterJewel &&
      baseItem.cluster_jewel.passive_skills.find((sk) => sk.id === source.clusterJewel!.skill)
    ) {
      item.clusterJewel.skill = source.clusterJewel.skill
    }
  }

  if (!source) {
    item.mods = {
      implicit: {},
      explicit: {},
      crafted: {},
      enchant: {},
      fractured: {}
    }
    return itemFixImplicit(item)
  }

  const mods = itemGetMods(item)

  function copyMods(type: StatType) {
    const srcMods = source!.mods![type]
    if (!srcMods) return {}
    const result: Record<string, Record<string, number>> = {}
    for (const [id, src] of Object.entries(srcMods)) {
      if (mods[type]?.[id]) result[id] = src
    }
    return result
  }
  function copyStats(type: StatType) {
    const srcStats = source!.stats[type]
    if (!srcStats) return {}
    //const stats = statListFromModList(mods[type]);
    const result: Record<string, number> = {}
    for (const [id, src] of Object.entries(srcStats)) {
      //if (stats[id]) result[id] = forceCondition(stats[id], src);
      result[id] = src
    }
    return result
  }

  if (source.mods) {
    item.mods = {
      enchant: copyMods('enchant'),
      implicit: copyMods('implicit')
    }
    itemUpdateStats(item, 'implicit', 'enchant')
    if (!source.unique) {
      if (source.mods.explicit) {
        item.mods.explicit = copyMods('explicit')
        item.mods.crafted = copyMods('crafted')
        item.mods.fractured = copyMods('fractured')
        itemUpdateStats(item, 'explicit', 'crafted', 'fractured')
      } else {
        item.stats.explicit = copyStats('explicit')
        item.stats.crafted = copyStats('crafted')
        item.stats.fractured = copyStats('fractured')
      }
    }
  } else {
    item.stats = source.stats
  }

  return itemFixSockets(itemFixImplicit(item))
}

export function itemCreateUnique(name: string, source?: Item, variant?: number) {
  const unique = Data.uniques[name]
  if (!unique) return
  if (variant == null || !unique.variants[variant]) {
    variant = unique.variants.length - 1
  }
  const variantData = unique.variants[variant]

  const item: Item = {
    ...itemCreateAbstract(variantData.base, source),
    unique: name,
    rarity: source?.rarity === 'foil' ? 'foil' : 'unique',
    variant,
    flags: unique.flags,
    stats: {
      enchant: {},
      implicit: {},
      explicit: {},
      crafted: {},
      fractured: {}
    }
  }

  if (Data.items[item.base].tier) {
    item.map_tier = variantData.map_tier ?? Data.items[item.base].tier
  }

  if (variantData.custom) {
    item.customText = variantData.custom
  }

  if (!source || source.mods) {
    item.mods = {
      implicit: {},
      enchant: {}
    }
    if (source) {
      const mods = itemGetMods(item, undefined, source.unique !== item.unique)
      for (const [type, values] of Object.entries(source.mods!)) {
        if (!values || !mods[type as StatType]) continue
        const res = Object.fromEntries(Object.entries(values).filter(([id]) => mods[type as StatType]![id]))
        if (Object.keys(res).length) item.mods[type as StatType] = res
      }
    }
    if (variantData.random_mods) {
      for (const mod of variantData.random_mods) {
        const type = mod.statType ?? 'explicit'
        if (!item.mods[type]) item.mods[type] = {}
      }
    }
  } else {
    item.stats.implicit = source.stats.implicit
    item.stats.enchant = source.stats.enchant
    if (source.unique === name) {
      item.stats.crafted = source.stats.crafted
      item.stats.explicit = source.stats.explicit
      item.stats.fractured = source.stats.fractured
      item.stats.custom = source.stats.custom
    }
  }

  item.stats.unique = {}
  for (const [id, src] of Object.entries(variantData.stats)) {
    if (typeof src === 'object') {
      if (source && source.unique === name && source.stats.unique?.[id]) {
        item.stats.unique[id] = forceCondition(src, source.stats.unique[id])
      } else {
        item.stats.unique[id] = src.max
      }
    } else {
      item.stats.unique[id] = src
    }
  }
  return itemFixSockets(itemFixImplicit(item))
}

export function itemGetName(item: Item) {
  if (item.unique) {
    return Data.uniques[item.unique].name
  } else if (item.rarity === 'normal') {
    return Data.items[item.base].name
  } else if (item.rarity === 'magic') {
    let name = Data.items[item.base].name
    if (item.mods && item.mods.explicit) {
      for (const id in item.mods.explicit) {
        const mod = Data.mods[id]
        if (mod.generation_type === 'prefix') name = `${mod.name} ${name}`
        if (mod.generation_type === 'suffix') name = `${name} ${mod.name}`
      }
    }
    return name
  } else {
    return item.name || Data.items[item.base].name
  }
}

export function itemTags(item: Item) {
  const base = Data.items[item.base]
  const tags = new Set(base.tags)
  const cls = Data.item_classes[base.item_class]
  if (cls.influence) {
    if (item.flags & ItemFlags.Shaper && cls.influence[0]) tags.add(cls.influence[0])
    if (item.flags & ItemFlags.Elder && cls.influence[1]) tags.add(cls.influence[1])
    if (item.flags & ItemFlags.Crusader && cls.influence[2]) tags.add(cls.influence[2])
    if (item.flags & ItemFlags.Redeemer && cls.influence[3]) tags.add(cls.influence[3])
    if (item.flags & ItemFlags.Hunter && cls.influence[4]) tags.add(cls.influence[4])
    if (item.flags & ItemFlags.Warlord && cls.influence[5]) tags.add(cls.influence[5])
  }
  if (base.cluster_jewel && item.clusterJewel) {
    const skill = base.cluster_jewel.passive_skills.find((sk) => sk.id === item.clusterJewel?.skill)
    if (skill) tags.add(skill.tag)
  }
  if (base.item_class === 'Map' && base.tier) {
    const tier = item.map_tier ?? base.tier
    if (tier >= 17) tags.add('uber_tier_map')
    else if (tier >= 11) tags.add('top_tier_map')
    else if (tier >= 6) tags.add('mid_tier_map')
    else tags.add('low_tier_map')
  }
  return tags
}

function matchingDomain(item_domain: string, mod_domain: string, mod_type: string) {
  return (
    item_domain === mod_domain ||
    (item_domain === 'affliction_jewel' && mod_domain === 'base_jewel' && mod_type === 'corrupted')
  )
}

export function itemGetMods(item: Item, full_mods?: boolean, skip_unique?: boolean) {
  const mods: {
    implicit: Record<string, ModDefinition>
    enchant: Record<string, ModDefinition>
    explicit?: Record<string, ModDefinition>
    crafted?: Record<string, ModDefinition>
    fractured?: Record<string, ModDefinition>
  } = {
    implicit: {},
    enchant: {}
  }
  const gen_types: Record<string, StatType> = {
    corrupted: 'implicit',
    enchantment: 'enchant'
  }
  if (!item.unique) {
    gen_types.prefix = 'explicit'
    gen_types.suffix = 'explicit'
  }
  if (itemAlwaysCorrupted(item)) {
    delete gen_types.corrupted
  }

  const base = Data.items[item.base]
  const tags = itemTags(item)

  for (const mod of base.implicits) {
    if (Data.mods[mod].visible) mods.implicit[mod] = Data.mods[mod]
  }
  for (const [id, mod] of Object.entries(Data.mods)) {
    if (matchingDomain(base.domain, mod.domain, mod.generation_type)) {
      const type = gen_types[mod.generation_type]
      if (!type) continue
      if (!evaluateTags(mod.spawn_weights, tags)) continue
      if (!full_mods && item.ilvl && item.ilvl < mod.required_level) continue
      if (!mods[type]) mods[type] = {}
      mods[type]![id] = mod
    }
  }
  if (!item.unique) {
    if (!mods.explicit) mods.explicit = {}
    if (!mods.crafted) mods.crafted = {}
    for (const id in Data.essences) {
      let mod = Data.essences[id].mods[base.item_class]
      if (mod) {
        if ((full_mods || !mods.explicit[mod]) && Data.mods[mod].essence_version) {
          mod = Data.mods[mod].essence_version!
        }
        mods.explicit[mod] = Data.mods[mod]
      }
    }
    for (const id in Data.crafting_bench_options) {
      const opt = Data.crafting_bench_options[id]
      if (opt.level > 100) continue
      if (!opt.item_classes.length || opt.item_classes.indexOf(base.item_class) >= 0) {
        let mod_id = opt.actions?.add_explicit_mod
        if (mod_id && Data.mods[mod_id]) mods.crafted![mod_id] = Data.mods[mod_id]
        mod_id = opt.actions?.add_enchant_mod
        if (mod_id && Data.mods[mod_id]) mods.enchant[mod_id] = Data.mods[mod_id]
      }
    }
  }

  const variant = item.unique && Data.uniques[item.unique].variants[item.variant!]
  if (variant && variant.random_mods && !skip_unique) {
    for (const group of variant.random_mods) {
      const list = group.type === 'list' ? group.list : uniqueModTypes[group.type].list()
      const statType = group.statType ?? 'explicit'
      if (statType === 'implicit' && !(item.flags & ItemFlags.Corrupted)) mods[statType] = {}
      else if (!mods[statType]) mods[statType] = {}
      for (const id of list) {
        mods[statType]![id] = Data.mods[id]
      }
    }
  }
  return mods
}

export function itemHasStatLine(item: Item, idx: number, type: StatType) {
  return Trans.all[idx].ids.some((id) => item.stats[type].hasOwnProperty(id))
}

type DamageType = 'physical' | 'fire' | 'cold' | 'lightning' | 'chaos'

const attributeTypes = ['strength', 'dexterity', 'intelligence'] as const
const defenseTypes = ['armour', 'evasion', 'energy_shield'] as const
const damageTypes = ['physical', 'fire', 'cold', 'lightning', 'chaos'] as const

export type ItemComputedProperties = {
  level_requirement: number
  attribute_requirements: {
    strength: number
    dexterity: number
    intelligence: number
  }
  base_level_requirement: number
  armour_stats?: {
    armour: number
    evasion: number
    energy_shield: number
  }
  weapon_stats?: {
    damage: {
      [type in DamageType]?: Range
    }
    crit: number
    speed: number
    range: number
  }
  block_chance?: number
  flask_stats?: {
    max_charges: number
    use_charges: number
    duration: number
    recovery?: {
      life?: number
      mana?: number
    }
  }
  tincture_stats?: {
    debuff_interval: number
    cooldown: number
  }
  map_stats?: {
    tier: number
    item_quantity: number
    item_rarity: number
    pack_size: number
  }
}

// calculate item properties such as requirements, damage and defense
export function itemGetProperties(item: Item, stats: LocalStats) {
  const base = Data.items[item.base]

  const out: ItemComputedProperties = {
    level_requirement: base.drop_level,
    attribute_requirements: { strength: 0, dexterity: 0, intelligence: 0 },
    base_level_requirement: 0
  }

  if (item.unique) {
    out.level_requirement = Math.max(out.level_requirement, Data.uniques[item.unique].level || 0)
  }
  if (item.mods) {
    for (const mods of Object.values(item.mods).filter(notNull)) {
      out.level_requirement = Math.max(
        out.level_requirement,
        ...Object.keys(mods).map((id) => Math.floor(Data.mods[id].required_level * 0.8))
      )
    }
  }
  if (base.properties && base.properties.charges_max) {
    out.level_requirement = Math.max(out.level_requirement, base.drop_level)
  }
  out.base_level_requirement = out.level_requirement

  if (base.requirements) {
    out.attribute_requirements.strength = base.requirements.strength || 0
    out.attribute_requirements.dexterity = base.requirements.dexterity || 0
    out.attribute_requirements.intelligence = base.requirements.intelligence || 0
  }

  out.level_requirement += stats.additive.level_requirement || 0
  if (out.level_requirement < 0 || stats.additive.no_level_requirement) {
    out.level_requirement = 0
  }

  const attribute_requirement = stats.additive.attribute_requirement
  const increased_attribute_requirement = stats.additive.increased_attribute_requirement
  for (const stat of attributeTypes) {
    out.attribute_requirements[stat] = Math.floor(
      (out.attribute_requirements[stat] + (attribute_requirement?.[stat] ?? 0)) *
        (1 + 0.01 * (increased_attribute_requirement?.[stat] ?? 0))
    )
    if (out.attribute_requirements[stat] < 0) out.attribute_requirements[stat] = 0
  }

  if (base.properties && (base.properties.armour || base.properties.evasion || base.properties.energy_shield)) {
    const defense = stats.additive.defense || {}
    const increased_defense = stats.additive.increased_defense || {}
    const no_defense = stats.additive.no_defense || {}
    out.armour_stats = { armour: 0, evasion: 0, energy_shield: 0 }
    for (const type of defenseTypes) {
      let value = base.properties[type] || 0
      if (typeof value === 'object') value = item.properties?.[type] || value.max
      value = Math.floor((value + (defense[type] || 0)) * (1 + 0.01 * (increased_defense[type] || 0)))
      if (value < 0 || no_defense[type]) value = 0
      out.armour_stats[type] = value
    }
  }
  if (base.properties && base.properties.attack_time) {
    out.weapon_stats = {
      damage: {},
      crit: Math.floor(
        (base.properties.critical_strike_chance ?? 0) * (1 + 0.01 * (stats.additive.increased_critical_chance ?? 0))
      ),
      speed: Math.floor((base.properties.attack_time ?? 0) / (1 + 0.01 * (stats.additive.increased_attack_speed ?? 0))),
      range: (base.properties.range ?? 0) + (stats.additive.weapon_range ?? 0)
    }

    if (stats.additive.always_crit) {
      out.weapon_stats.crit = 10000
    }

    const damage_min = stats.additive.damage_min
    const damage_max = stats.additive.damage_max
    const increased_damage = stats.additive.increased_local_damage
    for (const type of damageTypes) {
      let min = 0,
        max = 0
      if (type === 'physical') {
        min = base.properties.physical_damage_min ?? 0
        max = base.properties.physical_damage_max ?? 0
      }
      min = Math.max(0, Math.floor((min + (damage_min?.[type] ?? 0)) * (1 + 0.01 * (increased_damage?.[type] ?? 0))))
      max = Math.max(0, Math.floor((max + (damage_max?.[type] ?? 0)) * (1 + 0.01 * (increased_damage?.[type] ?? 0))))
      if (min || max) out.weapon_stats.damage[type] = { min: min, max: max }
    }
  }
  if (base.properties && base.properties.block) {
    out.block_chance = (base.properties.block || 0) + (stats.additive.block_chance || 0)
    if (out.block_chance < 0 || stats.additive.no_block_chance) out.block_chance = 0
  }

  if (base.properties && base.properties.charges_max) {
    out.flask_stats = {
      max_charges: base.properties.charges_max + (stats.additive.flask_max_charges || 0),
      use_charges: Math.floor(
        (base.properties.charges_per_use ?? 0) * (1 + 0.01 * (stats.additive.increased_flask_charges_used || 0))
      ),
      duration: Math.round(
        ((base.properties.duration ?? 0) * (1 + 0.01 * (stats.additive.increased_flask_duration || 0))) /
          (1 + 0.01 * (stats.additive.increased_flask_recovery_speed || 0))
      )
    }

    const increased_recovery = stats.additive.increased_flask_recovery || {}
    if (base.properties.life_per_use || base.properties.mana_per_use) {
      out.flask_stats.recovery = {}
      if (base.properties.life_per_use)
        out.flask_stats.recovery.life = Math.floor(
          base.properties.life_per_use * (1 + 0.01 * (increased_recovery.life || 0))
        )
      if (base.properties.mana_per_use)
        out.flask_stats.recovery.mana = Math.floor(
          base.properties.mana_per_use * (1 + 0.01 * (increased_recovery.mana || 0))
        )
    }
  }

  if (base.properties && base.properties.tincture_cooldown) {
    out.tincture_stats = {
      debuff_interval: base.properties.tincture_debuff_interval ?? 0,
      cooldown: base.properties.tincture_cooldown
    }
  }

  if (base.item_class === 'Map') {
    out.map_stats = {
      tier: item.map_tier ?? base.tier ?? 0,
      item_quantity: item.quality,
      item_rarity: 0,
      pack_size: 0
    }
    for (const cat of Object.values(item.stats)) {
      if (!cat) continue
      out.map_stats.item_quantity += cat['map_item_drop_quantity_+%'] ?? 0
      out.map_stats.item_rarity += cat['map_item_drop_rarity_+%'] ?? 0
      out.map_stats.pack_size += cat['map_pack_size_+%'] ?? 0
    }
  }

  return out
}

// format base item properties for tooltips or item info
export function itemFormatProperties(
  item: Item,
  props: RangeObject<ItemComputedProperties> | ItemComputedProperties,
  tooltip?: boolean
) {
  const base = Data.items[item.base]

  const is_equal = (value: number | Range, base: number = 0) =>
    typeof value === 'object' ? value.min === base && value.max === base : value === base
  const get_range = (value: number | Range, side: 'min' | 'max') => (typeof value === 'object' ? value[side] : value)

  const fmt_number = (value: number | Range, prec: number = 0, mult: number = 1): string =>
    typeof value === 'object'
      ? `(${fmt_number(value.min, prec, mult)}-${fmt_number(value.max, prec, mult)})`
      : (value * mult).toFixed(prec)

  const fmt_span = (text: string | number, style: string) => `<${style}>{${text}}`
  const fmt_value = (text: string | number, augmented?: boolean, def: string = 'Default') =>
    fmt_span(text, 'colour' + (augmented ? 'Augmented' : def))
  const fmt_compare = (value: number | Range, base?: number, prec?: number, mult?: number) =>
    fmt_value(fmt_number(value, prec, mult), !is_equal(value, base))
  const fmt_dps = (min: number, max: number) => (min === max ? min.toFixed(1) : min.toFixed(1) + '-' + max.toFixed(1))
  const tip_dps = (min: number, max: number) => fmt_span(` (${fmt_dps(min, max)} DPS)`, 'tipDPS')

  let lines: TooltipLine[] = []
  const add_line = (text: string, type: string = 'displayProperty') => lines.push({ text, type })

  if (props.map_stats) {
    add_line(`Map Level: ${fmt_value(67 + getValue(props.map_stats.tier, 'max'))}`)
    add_line(`Map Tier: ${fmt_value(fmt_number(props.map_stats.tier))}`)
    if (props.map_stats.item_quantity) {
      add_line(`Item Quantity: ${fmt_value('+' + fmt_number(props.map_stats.item_quantity) + '%', true)}`)
    }
    if (props.map_stats.item_rarity) {
      add_line(`Item Rarity: ${fmt_value('+' + fmt_number(props.map_stats.item_rarity) + '%', true)}`)
    }
    if (props.map_stats.pack_size) {
      add_line(`Monster Pack Size: ${fmt_value('+' + fmt_number(props.map_stats.pack_size) + '%', true)}`)
    }
  }

  if (tooltip) {
    if (props.weapon_stats) add_line(base.item_class)
    if (item.quality) add_line(`Quality: +${fmt_value(item.quality.toFixed(0), true)}%`)
  }

  if (base.talisman_tier) {
    add_line(`Talisman Tier: ${fmt_value(base.talisman_tier)}`)
  }

  if (props.block_chance) {
    let value = base.properties?.block ?? 0
    //if (typeof value === 'object') value = value.max
    add_line(
      `Chance to Block: ${fmt_value(fmt_number(props.block_chance) + '%', !is_equal(props.block_chance, value))}`
    )
  }

  if (props.armour_stats) {
    const printouts = {
      armour: 'Armour: ',
      evasion: 'Evasion: ',
      energy_shield: 'Energy Shield: '
    }
    for (const type of defenseTypes) {
      if (props.armour_stats[type]) {
        const value = props.armour_stats[type],
          orig = base.properties?.[type] || 0
        let equal: boolean
        if (typeof orig === 'object') {
          if (typeof value === 'object') {
            equal = value.min === orig.min && value.max === orig.max
          } else {
            equal = value === (item.properties?.[type] || 0)
          }
        } else {
          equal = is_equal(value, orig)
        }
        add_line(printouts[type] + fmt_value(fmt_number(value), !equal))
      }
    }
  }

  if (props.weapon_stats) {
    if (props.weapon_stats.damage.physical) {
      const value = props.weapon_stats.damage.physical
      const dps_min =
        ((1000 / get_range(props.weapon_stats.speed, 'max')) *
          (get_range(value.min, 'min') + get_range(value.max, 'min'))) /
        2
      const dps_max =
        ((1000 / get_range(props.weapon_stats.speed, 'min')) *
          (get_range(value.min, 'max') + get_range(value.max, 'max'))) /
        2
      const augmented = !(
        is_equal(value.min, base.properties.physical_damage_min) &&
        is_equal(value.max, base.properties.physical_damage_max)
      )
      add_line(
        `Physical Damage: ${fmt_value(
          fmt_number(value.min) + '-' + fmt_number(value.max),
          augmented,
          'PhysicalDamage'
        )}` + tip_dps(dps_min, dps_max)
      )
    }
    let dps_min = 0,
      dps_max = 0
    const elems = []
    const elemStyle = {
      fire: 'colourFireDamage',
      cold: 'colourColdDamage',
      lightning: 'colourLightningDamage'
    }
    for (const type of damageTypes) {
      if (type !== 'physical' && type !== 'chaos') {
        const value = props.weapon_stats.damage[type]
        if (!value) continue
        dps_min +=
          ((1000 / get_range(props.weapon_stats.speed, 'max')) *
            (get_range(value.min, 'min') + get_range(value.max, 'min'))) /
          2
        dps_max +=
          ((1000 / get_range(props.weapon_stats.speed, 'min')) *
            (get_range(value.min, 'max') + get_range(value.max, 'max'))) /
          2
        elems.push(fmt_span(fmt_number(value.min) + '-' + fmt_number(value.max), elemStyle[type]))
      }
    }
    if (elems.length) {
      add_line(`Elemental Damage: ${elems.join(', ')}` + tip_dps(dps_min, dps_max))
    }
    if (props.weapon_stats.damage.chaos) {
      const value = props.weapon_stats.damage.chaos
      const dps_min =
        ((1000 / get_range(props.weapon_stats.speed, 'max')) *
          (get_range(value.min, 'min') + get_range(value.max, 'min'))) /
        2
      const dps_max =
        ((1000 / get_range(props.weapon_stats.speed, 'min')) *
          (get_range(value.min, 'max') + get_range(value.max, 'max'))) /
        2
      add_line(
        `Chaos Damage: ${fmt_span(fmt_number(value.min) + '-' + fmt_number(value.max), 'colourChaosDamage')}` +
          tip_dps(dps_min, dps_max)
      )
    }

    add_line(
      `Critical Strike Chance: ${fmt_value(
        fmt_number(props.weapon_stats.crit, 2, 0.01) + '%',
        !is_equal(props.weapon_stats.crit, base.properties.critical_strike_chance)
      )}`
    )

    let aps
    if (typeof props.weapon_stats.speed === 'object') {
      aps = {
        min: 1000 / props.weapon_stats.speed.max,
        max: 1000 / props.weapon_stats.speed.min
      }
    } else {
      aps = 1000 / props.weapon_stats.speed
    }
    add_line(
      `Attacks per Second: ${fmt_value(
        fmt_number(aps, 2),
        !is_equal(props.weapon_stats.speed, base.properties.attack_time)
      )}`
    )

    if (!base.tags.includes('ranged')) {
      add_line(`Range: ${fmt_compare(props.weapon_stats.range, base.properties.range)}`)
    }
  }

  if (props.flask_stats) {
    const duration = fmt_compare(props.flask_stats.duration, base.properties.duration, 2, 0.1)
    if (props.flask_stats.recovery) {
      const values = props.flask_stats.recovery
      if (values.life)
        add_line(`Recovers ${fmt_compare(values.life, base.properties.life_per_use)} Life over ${duration} seconds`)
      if (values.mana)
        add_line(`Recovers ${fmt_compare(values.mana, base.properties.mana_per_use)} Mana over ${duration} seconds`)
    } else {
      add_line(`Lasts ${duration} seconds`)
    }
    add_line(
      `Consumes ${fmt_compare(props.flask_stats.use_charges, base.properties.charges_per_use)} of ${fmt_compare(
        props.flask_stats.max_charges,
        base.properties.charges_max
      )} Charges on use`
    )
    if (base.grants_buff && base.grants_buff.stats) {
      for (const stat of Trans.global.formatStats(base.grants_buff.stats)) {
        add_line(stat, 'utilityMod')
      }
    }
  }

  if (props.tincture_stats) {
    add_line(
      `Inflicts Mana Burn every ${fmt_value(fmt_number(props.tincture_stats.debuff_interval, 2, 1 / 1000))} Seconds`
    )
    add_line(`${fmt_value(fmt_number(props.tincture_stats.cooldown, 2, 1 / 1000))} Second Cooldown when Deactivated`)
  }

  const unique = item.unique != null && Data.uniques[item.unique]
  if (unique && unique.jewel_limit) {
    let limit = unique.jewel_limit.toString()
    if (unique.jewel_limit_type === 'historic') limit += ' Historic'
    if (unique.jewel_limit_type === 'survival') limit += ' Survival'
    add_line(`Limited to: ${fmt_value(limit)}`)
  }

  let radius = base?.tags.includes('radius_jewel') ? 2 : 0
  if (unique && unique.jewel_radius) {
    radius = unique.jewel_radius
  }
  if (item.stats.explicit?.local_jewel_effect_base_radius) {
    let value = radius ? Data.jewel_radii[radius - 1].radius : 0
    value += item.stats.explicit.local_jewel_effect_base_radius
    while (Data.jewel_radii[radius] && Data.jewel_radii[radius].radius <= value) {
      radius += 1
    }
  }
  if (radius) {
    const entry = Data.jewel_radii[radius - 1]
    if (entry) add_line(`Radius: ${fmt_value(entry.name)}`)
  } else if (item.stats.explicit?.local_jewel_variable_ring_radius_value) {
    add_line(`Radius: ${fmt_value('Variable', true)}`)
  }

  const properties = lines

  const reqs = []
  if (get_range(props.level_requirement, 'max') > 1) {
    reqs.push(`Level ${fmt_compare(props.level_requirement, get_range(props.base_level_requirement, 'max'))}`)
  }
  if (props.attribute_requirements) {
    const printouts = {
      strength: ' Str',
      dexterity: ' Dex',
      intelligence: ' Int'
    }
    for (const stat of attributeTypes) {
      const value = props.attribute_requirements[stat]
      const orig = (base.requirements && base.requirements[stat]) || 0
      if (get_range(value, 'max') > 14) reqs.push(fmt_compare(value, orig) + printouts[stat])
    }
  }

  if (base.properties && base.properties.stack_size) {
    add_line(`Stack Size: ${fmt_value(base.properties.stack_size)}`)
  }

  if (base.tattoo) {
    add_line(`Replaces a ${fmt_value(tattooTargetSets[base.tattoo.set])} Passive Skill`)
    const tattoo = Data.tattoos[base.tattoo.id]
    if (tattoo.limit) {
      add_line(`Limited To: ${fmt_value(tattooLimits[tattoo.limit])}`)
    }
  }

  lines = []

  if (base.tattoo) {
    const tattoo = Data.tattoos[base.tattoo.id]
    if (tattoo.requiresAdjacent) {
      add_line(`Requires ${fmt_value(tattoo.requiresAdjacent)} adjacent Passive Skills Allocated`)
    }
    if (tattoo.maxAdjacent) {
      add_line(`Requires ${fmt_value(`maximum ${tattoo.maxAdjacent}`)} adjacent Passive Skill Allocated`)
    }
  }

  if (tooltip && item.ilvl) {
    add_line(`Item Level: ${fmt_value(item.ilvl)}`)
  }
  if (reqs.length) {
    add_line(`Requires ${reqs.join(', ')}`, 'requirements')
  }

  const grantsNotable =
    item.stats.explicit.unique_jewel_grants_notable_hash_part_1 ??
    item.stats.explicit.unique_jewel_grants_notable_hash_part_2
  if (grantsNotable) {
    if (grantsNotable === -2) {
      add_line(`Requires Class: ${fmt_value('<Random Character Class>')}`)
    } else {
      const node = treeNode(grantsNotable)
      const charClass = node?.ascendancy && Data.ascendancyClass[node.ascendancy]
      if (charClass) {
        add_line(`Requires Class: ${fmt_value(Data.characters[charClass].name)}`)
      }
    }
  }

  return {
    properties,
    requirements: lines
  }
}

export function itemGetStats(item: Item, env?: Environment, tags?: Set<string>) {
  const base = Data.items[item.base]
  const stats = new LocalStats(env, tags)
  for (const src of Object.values(item.stats).filter(notNull)) {
    for (const [id, value] of Object.entries(src)) {
      stats.add(id, value)
    }
  }
  if (
    base.properties &&
    (base.properties.armour || base.properties.evasion || base.properties.energy_shield) &&
    item.quality
  ) {
    stats.add('item_quality_armour', item.quality)
  }
  if (base.properties && base.properties.attack_time && item.quality) {
    stats.add('item_quality_weapon', item.quality)
  }
  if (base.properties && base.properties.charges_max && item.quality) {
    if (base.properties.life_per_use || base.properties.mana_per_use) {
      stats.add('item_quality_flask', item.quality)
    } else {
      stats.add('item_quality_flask_utility', item.quality)
    }
  }

  return stats
}

export function itemResetMods(item: Item) {
  if (!item.mods) return item
  item = { ...item }
  delete item.mods
  return item
}

export function itemMaxAffixes(item: Item) {
  if (item.unique) {
    return 0
  } else if (item.rarity === 'magic') {
    return 1
  } else if (item.rarity === 'rare') {
    if (['Jewel', 'AbyssJewel'].includes(Data.items[item.base].item_class)) {
      return 2
    } else if (Data.items[item.base].item_class === 'Map') {
      return 4
    } else {
      return 3
    }
  } else {
    return 0
  }
}

export function itemCreate(name: string, source?: Item, variant?: number) {
  if (Data.items[name]) {
    return itemCreateNormal(name, source)
  } else if (Data.uniques[name]) {
    return itemCreateUnique(name, source, variant)
  }
}

export function itemUniqueProps(name: string, variantIndex?: number) {
  const item = itemCreateUnique(name, undefined, variantIndex)
  if (!item) return
  item.ilvl = 0
  if (baseMaxQuality(item.base)) item.quality = 0
  const propsMax = itemGetProperties(item, itemGetStats(item))
  for (const [id, mod] of Object.entries(item.mods!.implicit)) {
    for (const stat of Data.mods[id].stats) {
      mod[stat.id] = stat.min
    }
  }
  const variant = Data.uniques[item.unique!].variants[item.variant!]
  const base = Data.items[variant.base]
  if (base?.properties) {
    for (const prop of defenseTypes) {
      const value = base.properties[prop]
      if (value && typeof value === 'object') item.properties[prop] = value.min
    }
  }
  item.stats.unique = { ...item.stats.unique }
  for (const [id, value] of Object.entries(variant.stats)) {
    item.stats.unique[id] = typeof value === 'object' ? value.min : value
  }
  itemUpdateStats(item)
  const propsMin = itemGetProperties(item, itemGetStats(item))
  const props = modMakeRange(propsMin, propsMax)
  return itemFormatProperties(item, props, true)
}

export function itemTemplateProps(item: Item) {
  const itemMin = itemCreateNormal(item.base, item)
  if (item.map_tier) itemMin.map_tier = item.map_tier
  const base = Data.items[item.base]
  if (base?.properties) {
    itemMin.properties = { ...itemMin.properties }
    for (const prop of defenseTypes) {
      const value = base.properties[prop]
      if (value && typeof value === 'object') item.properties[prop] = value.min
    }
  }
  const propsMax = itemGetProperties(item, itemGetStats(item))
  const propsMin = itemGetProperties(itemMin, itemGetStats(itemMin))
  return modMakeRange(propsMin, propsMax)
}

export function itemEssenceMods(essence: EssenceDefinition) {
  const lines: {
    name: string
    mod: string
  }[] = []

  const weapons = {
    melee1h: essence.mods['One Hand Axe'],
    melee2h: essence.mods['Two Hand Axe'],
    wand: essence.mods['Wand'],
    bow: essence.mods['Bow']
  }
  if (Object.values(weapons).every((mod) => mod === weapons.melee1h)) {
    lines.push({ name: 'Weapon', mod: weapons.melee1h })
  } else if (weapons.melee1h === weapons.melee2h && weapons.wand === weapons.bow) {
    lines.push({ name: 'Melee Weapon', mod: weapons.melee1h })
    lines.push({ name: 'Ranged Weapon', mod: weapons.wand })
  } else if (weapons.melee1h === weapons.wand && weapons.melee2h === weapons.bow) {
    lines.push({ name: 'One Handed Weapon', mod: weapons.melee1h })
    lines.push({ name: 'Two Handed Weapon', mod: weapons.melee2h })
  } else if (weapons.melee1h === weapons.melee2h && weapons.melee1h === weapons.wand) {
    lines.push({ name: 'Bow', mod: weapons.bow })
    lines.push({ name: 'Other Weapon', mod: weapons.melee1h })
  } else if (weapons.melee1h === weapons.melee2h && weapons.melee1h === weapons.bow) {
    lines.push({ name: 'Wand', mod: weapons.wand })
    lines.push({ name: 'Other Weapon', mod: weapons.melee1h })
  } else if (weapons.melee1h === weapons.wand) {
    lines.push({ name: 'Bow', mod: weapons.bow })
    lines.push({ name: 'Two Handed Melee Weapon', mod: weapons.melee2h })
    lines.push({ name: 'Other Weapon', mod: weapons.melee1h })
  } else {
    lines.push({ name: 'Wand', mod: weapons.wand })
    lines.push({ name: 'Bow', mod: weapons.bow })
    lines.push({ name: 'Other One Handed Weapon', mod: weapons.melee1h })
    lines.push({ name: 'Two Handed Melee Weapon', mod: weapons.melee2h })
  }

  const armors = ['Gloves', 'Boots', 'Body Armours', 'Helmet', 'Shield']
  const armorCounts = armors.map((type) => armors.filter((a) => essence.mods[a] === essence.mods[type]).length)
  const bestArmorIndex = armorCounts.lastIndexOf(Math.max(...armorCounts))
  const baseArmorMod = essence.mods[armors[bestArmorIndex]]

  const jewelry = ['Quiver', 'Amulet', 'Ring', 'Belt']
  const jewelryCounts = jewelry.map((type) => jewelry.filter((a) => essence.mods[a] === essence.mods[type]).length)
  const bestJewelryIndex = jewelryCounts.lastIndexOf(Math.max(...jewelryCounts))
  const baseJewelryMod = essence.mods[jewelry[bestJewelryIndex]]

  if (
    armorCounts[bestArmorIndex] === armors.length &&
    jewelryCounts[bestJewelryIndex] === jewelry.length &&
    baseArmorMod === baseJewelryMod
  ) {
    lines.push({ name: 'Other Item', mod: baseArmorMod })
  } else {
    if (armorCounts[bestArmorIndex] === armors.length) {
      lines.push({ name: 'Armour', mod: baseArmorMod })
    } else {
      for (const type of armors) {
        if (essence.mods[type] !== baseArmorMod) {
          lines.push({ name: type, mod: essence.mods[type] })
        }
      }
      lines.push({ name: 'Other Armour', mod: baseArmorMod })
    }
    if (jewelryCounts[bestJewelryIndex] === jewelry.length) {
      lines.push({ name: 'Jewellery', mod: baseJewelryMod })
    } else {
      for (const type of jewelry) {
        if (essence.mods[type] !== baseJewelryMod || jewelryCounts[bestJewelryIndex] <= 1) {
          lines.push({ name: type, mod: essence.mods[type] })
        }
      }
      if (jewelryCounts[bestJewelryIndex] > 1) {
        lines.push({ name: 'Other Jewellery', mod: baseJewelryMod })
      }
    }
  }
  return lines
}

export function itemGetBaseColor(base: ItemDefinition) {
  if (base === Data.items['Metadata/Items/Currency/CurrencyItemisedSextantModifier']) return
  if (base.tags.includes('currency')) return 'currency'
  if (base.tags.includes('gem')) return 'gem'
  if (base.tags.includes('quest') || base.item_class === 'QuestItem') return 'quest'
  if (base.tags.includes('prophecy')) return 'prophecy'
  if (base.tags.includes('divination_card')) return 'divination'
}

export function itemColor(item: Item) {
  return itemGetBaseColor(Data.items[item.base]) || item.rarity || 'normal'
}

export function itemParseMods(item: Item) {
  const mods = itemGetMods(item)
  const result: Item['mods'] = {
    enchant: {},
    implicit: {}
  }
  const categories: StatType[] = ['enchant', 'implicit']
  categories.push('crafted', 'fractured', 'explicit')
  for (const category of categories) {
    if (!mods[category] && item.stats[category] && Object.keys(item.stats[category]).length) return item
    if (!mods[category]) continue
    const knownMods: Record<string, Record<string, number>> = {}
    if (Data.items[item.base].domain !== 'flask') {
      if (category === 'fractured' || category === 'explicit') Object.assign(knownMods, result.crafted)
    }
    if (category === 'explicit') Object.assign(knownMods, result.fractured)
    const parsed = parseMods(mods[category]!, item.stats[category], {
      max_affix: itemMaxAffixes(item),
      mods: Object.keys(knownMods),
      ignore_groups: item.unique != null
    })
    if (!parsed.length) return item
    result[category] = parseModStats(parsed[0], item.stats[category])
  }
  return { ...item, mods: result }
}

export function parseBase(line: string | undefined) {
  if (!line) return undefined
  let best: ItemDefinition | undefined
  let bestId: string | undefined
  for (const [id, item] of Object.entries(Data.items)) {
    if (item.name.length >= 3 && line.includes(item.name)) {
      if (!best || best.name.length < item.name.length) {
        best = item
        bestId = id
      }
    }
  }
  return bestId
}

function parseHeader(result: Item, line: string | undefined, lines: string[]) {
  let base = parseBase(lines[0])
  if (base) {
    result.name = line
    result.base = base
    lines.shift()
    return true
  }
  base = parseBase(line)
  if (!base) return false
  result.base = base
  //result.name = line;
  return true
}

let _sameNameCache: Record<string, string[]> | undefined
function sameNameCache() {
  if (!_sameNameCache) {
    _sameNameCache = {}
    for (const [id, item] of Object.entries(Data.items)) {
      _sameNameCache[item.name] = _sameNameCache[item.name] || []
      _sameNameCache[item.name].push(id)
    }
  }

  return _sameNameCache!
}

function matchingBase(item: Item) {
  const data = Data.items[item.base]
  if (data.properties) {
    if (data.properties.armour && !item.properties.armour) return false
    if (data.properties.evasion && !item.properties.evasion) return false
    if (data.properties.energy_shield && !item.properties.energy_shield) return false
  }
  const mods = itemGetMods(item)
  return parseMods(mods.implicit, item.stats.implicit).length > 0
}

export function itemParseText(text: string, forceMods?: boolean, pobMods?: boolean) {
  try {
    return JSON.parse(text)
  } catch (e) {
    // do nothing
  }
  let result: Item = {
    base: 'INVALID',
    rarity: 'normal',
    ilvl: 99,
    properties: {},
    sockets: [],
    stats: {
      implicit: {},
      explicit: {},
      crafted: {},
      enchant: {},
      fractured: {}
    },
    flags: 0,
    quality: 0
  }
  const lines = text
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  let line = lines.shift(),
    m
  if ((m = line?.match(/^Item Class: (.*)$/))) {
    // do nothing?
    line = lines.shift()
  }
  let hasRarity = false
  if ((m = line?.match(/^Rarity: (.*)$/))) {
    hasRarity = true
    result.rarity = m[1].toLowerCase() as ItemRarity
    line = lines.shift()
  }
  if (!parseHeader(result, line, lines)) {
    return
  }
  let block = null
  const statLines: { [key in StatType]?: string[] } = {}
  const rangeLines: { [key in StatType]?: { line: string; range: number }[] } = {}
  let implicits = 0
  let variant = 0
  const ignoreLines: string[] = []
  while (lines.length) {
    line = lines.shift()!
    if (ignoreLines.includes(line)) {
      // Don't parse passive descriptions from trade site
      break
    }
    if (line.match(/^-+$/)) {
      block = null
    } else if ((m = line.match(/^Armour: (\d+)/))) {
      result.properties.armour = parseInt(m[1])
    } else if ((m = line.match(/^Evasion Rating: (\d+)/))) {
      result.properties.evasion = parseInt(m[1])
    } else if ((m = line.match(/^Energy Shield: (\d+)/))) {
      result.properties.energy_shield = parseInt(m[1])
    } else if ((m = line.match(/^Quality: \+?(\d+)/))) {
      result.quality = parseInt(m[1])
    } else if (line.match(/^Requirements:$/)) {
      block = 'requirements'
    } else if (block === 'requirements') {
      // don't care
    } else if ((m = line.match(/^Sockets: (.*)$/))) {
      for (const [chr] of m[1].matchAll(/[RGBW]-?/g)) {
        const link = chr[1] === '-'
        if (chr[0] === 'R') result.sockets.push({ color: 'red', link })
        else if (chr[0] === 'G') result.sockets.push({ color: 'green', link })
        else if (chr[0] === 'B') result.sockets.push({ color: 'blue', link })
        else if (chr[0] === 'W') result.sockets.push({ color: 'white', link })
      }
    } else if ((m = line.match(/^Item Level: (\d+)/))) {
      result.ilvl = parseInt(m[1])
    } else if (line === 'Corrupted') {
      result.flags |= ItemFlags.Corrupted
    } else if ((m = line.match(/^Implicits: (\d+)/))) {
      implicits = parseInt(m[1])
    } else if ((m = line.match(/^Selected Variant: (\d+)/))) {
      variant = parseInt(m[1])
    } else {
      let type: StatType = 'explicit'
      let range: number | undefined
      let lineVariant: number | undefined
      if ((m = line.match(/^(.*) \((implicit|crafted|fractured|enchant)\)$/))) {
        line = m[1]
        type = m[2] as StatType
      }
      line = line.replace(/\{([^}]*)\}/g, (m, tag) => {
        if (['implicit', 'crafted', 'fractured', 'enchant'].includes(tag)) {
          type = tag as StatType
        }
        const rm = tag.match(/^range:(.*)$/)
        if (rm) {
          const value = parseFloat(rm[1])
          if (!isNaN(value)) range = value
        }
        const vm = tag.match(/^variant:(\d+)$/)
        if (vm) {
          lineVariant = parseInt(vm[1])
        }
        return ''
      })
      if (lineVariant != null && lineVariant !== variant) continue
      if (implicits) {
        if (type === 'crafted') {
          type = 'enchant'
        } else {
          type = 'implicit'
        }
        implicits -= 1
      }
      if (range != undefined) {
        rangeLines[type] = rangeLines[type] ?? []
        rangeLines[type]!.push({ line, range })
      } else {
        statLines[type] = statLines[type] ?? []
        statLines[type]!.push(line)
      }
    }
    if ((m = line.match(/^Allocates (.*)$/))) {
      ignoreLines.push(m[1])
    }
  }

  const base = Data.items[result.base]
  const template = Data.item_templates[base.inherits]
  const domain = template.translation_domain ?? 'global'

  const unparsed: Record<string, string[]> = {}
  for (const [type, lines] of Object.entries(statLines)) {
    unparsed[type] = []
    const parsed = Trans[domain].parseMultiLine({ lines, unparsed: unparsed[type] })
    if (parsed) {
      const values: Record<string, number> = {}
      for (const [stat, rangeOrValue] of Object.entries(parsed)) {
        values[stat] = typeof rangeOrValue === 'number' ? rangeOrValue : rangeOrValue.max
      }
      statsExtend(result.stats[type as StatType], parsed)
    }
  }
  for (const [type, lines] of Object.entries(rangeLines)) {
    for (const { line, range } of lines) {
      const parsed = Trans[domain].parseLine(line)
      if (!parsed) continue
      for (const [stat, rangeOrValue] of Object.entries(parsed)) {
        const value =
          typeof rangeOrValue === 'number'
            ? rangeOrValue
            : rangeOrValue.min + (rangeOrValue.max - rangeOrValue.min) * range
        statsExtend(result.stats[type as StatType], {
          [stat]: value
        })
      }
    }
  }

  if (result.rarity === 'unique') {
    const id = Object.keys(Data.uniques).find((id) => Data.uniques[id].name === result.name)
    if (id) {
      result.unique = id
      const data = Data.uniques[id]
      result.variant = data.variants.length - 1
      result.stats.unique = {}
      for (const id of Object.keys(data.variants[result.variant].stats)) {
        result.stats.unique[id] = result.stats.explicit[id]
        delete result.stats.explicit[id]
      }
    }
  }

  if (base.cluster_jewel && !result.unique) {
    result.clusterJewel = {
      nodes: base.cluster_jewel.min_skills,
      skill: base.cluster_jewel.passive_skills[0].id
    }
    if (result.stats.enchant.local_jewel_expansion_passive_node_count) {
      result.clusterJewel.nodes = result.stats.enchant.local_jewel_expansion_passive_node_count
      delete result.stats.enchant.local_jewel_expansion_passive_node_count
    }
    delete result.stats.enchant.local_jewel_expansion_jewels_count
    if (unparsed.enchant) {
      const lines: string[] = []
      for (const line of unparsed.enchant) {
        const m = line.match(/Added Small Passive Skills grant: (.*)/)
        if (m) lines.push(m[1])
      }
      const skill = base.cluster_jewel.passive_skills.find(({ id }) => {
        const text = Trans.global.formatStats(Data.passive_skills[id]?.stats ?? {})
        return text.every((txt) => lines.some((line) => line.includes(txt)))
      })
      if (skill != null) result.clusterJewel.skill = skill.id
    }
  }

  const sameName = sameNameCache()[base.name]
  if (sameName && sameName.length > 1) {
    const base = sameName.find((id) => matchingBase({ ...result, base: id }))
    if (base) result.base = base
  }

  itemFixSockets(result)

  if (!result.rarity) {
    result.rarity = 'normal'
    result = itemParseMods(result)
    if (!result.mods) {
      result.rarity = 'magic'
      result = itemParseMods(result)
    }
    if (!result.mods && !template.disable_rarity?.includes('rare')) {
      result.rarity = 'rare'
    }
  }
  result = itemParseMods(result)

  if (result.properties.armour || result.properties.evasion || result.properties.energy_shield) {
    const stats = itemGetStats(result)
    const defense = stats.additive.defense || {}
    const increased_defense = stats.additive.increased_defense || {}
    for (const type of defenseTypes) {
      if (!result.properties[type]) continue
      result.properties[type] =
        Math.round(result.properties[type]! / (1 + 0.01 * (increased_defense[type] || 0))) - (defense[type] || 0)
    }
  }

  return result
}

export function encodeTooltipOptions(options: ItemTooltipOptions) {
  let str = ''
  if (options.enchant != null) str += `e${options.enchant}`
  if (options.implicit != null) str += `i${options.implicit}`
  if (options.explicit != null) str += `x${options.explicit}`
  if (options.fractured != null) str += `f${options.fractured}`
  if (options.crafted != null) str += `c${options.crafted}`
  if (options.art != null) str += `a${options.art ? 1 : 0}`
  return str
}

export function decodeTooltipOptions(str: string) {
  const options: ItemTooltipOptions = {}
  for (const [, t, v] of str.matchAll(/([eixfca])(\d+)/g)) {
    const value = Number(v)
    switch (t) {
      case 'e':
        options.enchant = value
        break
      case 'i':
        options.implicit = value
        break
      case 'x':
        options.explicit = value
        break
      case 'f':
        options.fractured = value
        break
      case 'c':
        options.crafted = value
        break
      case 'a':
        options.art = value !== 0
        break
    }
  }
  return options
}

export function itemTranslationDomain(item: ItemDefinition) {
  if (!item) return 'global'
  const template = Data.item_templates[item.inherits]
  return template.translation_domain ?? 'global'
}

export function itemBaseIcon(item: ItemDefinition | UniqueDefinition | undefined, map_tier?: number, variant?: number) {
  if (!item) return
  if ('item_class' in item) {
    const tier = map_tier ?? item.tier
    const icon = item.visual_identity.dds_file
    if (tier) {
      if (tier >= 17) return icon.replace('/new/', '/new4/')
      else if (tier >= 11) return icon.replace('/new/', '/new3/')
      else if (tier >= 6) return icon.replace('/new/', '/new2/')
      else return icon.replace('/new/', '/new1/')
    } else {
      return icon
    }
  } else {
    const data = item.variants[variant ?? item.variants.length - 1]
    return data?.icon ?? item.icon
  }
}

export function itemIcon(item: Item) {
  const data = (item.unique && Data.uniques[item.unique]) || Data.items[item.base]
  return itemBaseIcon(data, item.map_tier, item.variant)
}

export function uniqueGroupName(mod: UniqueRandomMod, domain = 'global') {
  if (mod.placeholder) {
    if (mod.placeholder.match(/<.*>/)) return [mod.placeholder]
    return [`<${mod.placeholder}>`]
  }
  const list = mod.type === 'list' ? mod.list : uniqueModTypes[mod.type].list()
  const stats: Record<string, Range> = {}
  for (const mid of list) {
    if (!Data.mods[mid]) continue
    for (const { id, min, max } of Data.mods[mid]!.stats) {
      if (stats[id]) {
        stats[id].min = Math.min(min, stats[id].min)
        stats[id].max = Math.max(max, stats[id].max)
      } else {
        stats[id] = { min, max }
      }
    }
  }
  return Trans[domain].formatStats(stats)
}

export function itemMaxStats(_items: ItemDefinition[]) {
  const mods: Record<string, ModDefinition> = {}
  for (const [name, mod] of Object.entries(Data.mods)) mods[name] = mod

  return statListFromModList(mods)
}
